import moment from "moment";

// Children the signed-in caregiver has seen in the portal, remembered across
// visits so the child chips and the Welcome line don't wait for every day to load.
// Keyed by the Auth0 user so a shared browser never shows another family's children.
// Axon only returns children with visits in the loaded range, so there is no full
// child list to check against: a child not seen for CLIENT_EXPIRY_DAYS is dropped.

export interface CaregiverClient {
  id: number;
  name: string;
  // Local calendar day (YYYY-MM-DD) the child last appeared in loaded visits.
  lastSeen: string;
}

interface VisitClient {
  client_id?: number;
  client_name?: string;
}

const KEY_PREFIX = "caregiverClients:";
// Before AXON-3641 one key was shared by everyone who used the browser.
const LEGACY_SHARED_KEY = "caregiverClients";
export const CLIENT_EXPIRY_DAYS = 30;
const DAY_FORMAT = "YYYY-MM-DD";

const keyFor = (userId: string) => `${KEY_PREFIX}${userId}`;
const todayKey = () => moment().format(DAY_FORMAT);

const isStoredClient = (
  value: unknown
): value is { id: number; name: string; lastSeen?: string } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CaregiverClient).id === "number" &&
  typeof (value as CaregiverClient).name === "string";

const isValidDay = (value: unknown): value is string =>
  typeof value === "string" && moment(value, DAY_FORMAT, true).isValid();

const isExpired = (client: CaregiverClient, today: string) =>
  moment(today, DAY_FORMAT).diff(moment(client.lastSeen, DAY_FORMAT), "days") >
  CLIENT_EXPIRY_DAYS;

export const readCaregiverClients = (
  userId?: string,
  today: string = todayKey()
): CaregiverClient[] => {
  try {
    localStorage.removeItem(LEGACY_SHARED_KEY);
    if (!userId) return [];
    const parsed: unknown = JSON.parse(localStorage.getItem(keyFor(userId)) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isStoredClient)
      // Entries saved before lastSeen existed, or with a broken date, count as
      // seen today so they still expire in CLIENT_EXPIRY_DAYS.
      .map((client) => ({
        ...client,
        lastSeen: isValidDay(client.lastSeen) ? client.lastSeen : today,
      }))
      .filter((client) => !isExpired(client, today));
  } catch {
    return [];
  }
};

export const writeCaregiverClients = (
  userId: string | undefined,
  clients: CaregiverClient[]
) => {
  if (!userId) return;
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(clients));
  } catch {
    // Storage full or blocked: the chips just rebuild from loaded visits.
  }
};

// Adds children from newly loaded visits, refreshes when each was last seen,
// and drops anyone not seen for CLIENT_EXPIRY_DAYS. Returns `existing` itself
// when nothing changed, so callers can skip re-rendering and re-writing.
export const mergeCaregiverClients = (
  existing: CaregiverClient[],
  visits: VisitClient[],
  today: string = todayKey()
): CaregiverClient[] => {
  let changed = false;
  const byId = new Map(existing.map((client) => [client.id, client]));
  visits.forEach(({ client_id: id, client_name: name }) => {
    if (!id || !name) return;
    const known = byId.get(id);
    if (known && known.name === name && known.lastSeen === today) return;
    byId.set(id, { id, name, lastSeen: today });
    changed = true;
  });
  const kept = Array.from(byId.values()).filter((client) => !isExpired(client, today));
  if (kept.length !== byId.size) changed = true;
  return changed ? kept : existing;
};
